import type { LatLon, Waypoint } from "@radar-sim/schema";
import { distanceNm, projectPosition } from "./geodesy.js";

/**
 * Coastline-aware path router. Walks each tracked-vessel segment,
 * detects places where the straight-line path crosses land (per the
 * area's coastline polylines), and inserts intermediate waypoints
 * that deflect the path back into the water.
 *
 * Determinism contract: pure function of input waypoints + area data.
 * No randomness, no time-of-day reads. The transformation runs once
 * at scenario load time so the determinism guarantee on the snapshot
 * stream is preserved (every consumer sees the same routed waypoints).
 *
 * Scope: only tracked vessels. Behavioural vessels (fishing patterns,
 * random walks, lane followers) get coastline-awareness through their
 * runtime tick instead.
 */

/** Max recursion when bisecting a land-crossing segment. */
const MAX_DEPTH = 5;
/** Step size (degrees) when scanning a segment for the deepest land
 *  intrusion. Roughly 300m at UK latitudes — the same granularity the
 *  validator uses for its mid-segment sweep. */
const SCAN_STEP_DEG = 0.005;
/** Distances (nm) tried in order when nudging a midpoint perpendicular
 *  to the line to escape land. Small first so we deflect minimally. */
const NUDGE_DISTANCES_NM = [0.05, 0.1, 0.2, 0.3, 0.5, 0.8, 1.2, 2, 3];

/**
 * Predicate: is this position on land? Supplied by the caller (the
 * area-data package builds one for each area's simplified coastline).
 * Keeping it as an opaque function lets the engine stay area-data
 * agnostic — it routes around whatever the predicate considers land.
 */
export interface IsOnLand {
  (point: LatLon): boolean;
}

/**
 * Route a vessel's waypoints around land. Returns a new array with
 * intermediate waypoints inserted where needed. Each inserted
 * waypoint's `atSimSeconds` is interpolated proportionally to its
 * distance along the original segment, preserving the vessel's
 * scheduled arrival time at the original endpoints.
 *
 * If the input has fewer than two waypoints, returned unchanged.
 */
export function routeAroundLand(
  waypoints: ReadonlyArray<Waypoint>,
  isOnLand: IsOnLand,
): Waypoint[] {
  if (waypoints.length < 2) return [...waypoints];
  const out: Waypoint[] = [waypoints[0]!];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i]!;
    const b = waypoints[i + 1]!;
    const routed = routeSegment(a.position, b.position, isOnLand, 0);
    // routed[0] is `a.position`, routed[N-1] is `b.position`.
    // Insert any intermediate points with interpolated time + speed.
    const totalDistNm = pathDistanceNm(routed);
    const segmentDuration = b.atSimSeconds - a.atSimSeconds;
    let cumulativeNm = 0;
    for (let j = 1; j < routed.length - 1; j++) {
      cumulativeNm += distanceNm(routed[j - 1]!, routed[j]!);
      const fraction = totalDistNm > 0 ? cumulativeNm / totalDistNm : 0;
      out.push({
        atSimSeconds: a.atSimSeconds + fraction * segmentDuration,
        position: routed[j]!,
        // Inherit cog/sog from the start; the engine recomputes
        // effective course between waypoints anyway, so these are
        // primarily authoring metadata.
        cog: a.cog,
        sog: a.sog,
      });
    }
    out.push(b);
  }
  return out;
}

/**
 * Recursively bisect a segment until no sub-segment crosses land.
 * Returns the polyline from `a` to `b` inclusive.
 */
function routeSegment(
  a: LatLon,
  b: LatLon,
  isOnLand: IsOnLand,
  depth: number,
): LatLon[] {
  if (depth >= MAX_DEPTH) return [a, b];
  const land = scanForLand(a, b, isOnLand);
  if (land === undefined) return [a, b];
  // Pick the deepest-into-land point as the bisection seed and nudge
  // it perpendicular to the segment until it's in water.
  const seed = land.deepest;
  const nudged = nudgeOffLand(seed, a, b, isOnLand);
  if (nudged === undefined) {
    // Couldn't escape — return the straight line and let the
    // determinism contract hold. The validator will warn at build
    // time so authors can fix the underlying scenario.
    return [a, b];
  }
  const left = routeSegment(a, nudged, isOnLand, depth + 1);
  const right = routeSegment(nudged, b, isOnLand, depth + 1);
  return [...left.slice(0, -1), ...right];
}

/**
 * Sample the segment at SCAN_STEP_DEG granularity and return the
 * fraction `t` where land protrudes deepest into the path, or
 * undefined if the segment is fully in water.
 */
function scanForLand(
  a: LatLon,
  b: LatLon,
  isOnLand: IsOnLand,
): { deepest: LatLon } | undefined {
  const dlat = b.lat - a.lat;
  const dlon = b.lon - a.lon;
  const span = Math.max(Math.abs(dlat), Math.abs(dlon));
  const steps = Math.max(2, Math.ceil(span / SCAN_STEP_DEG));
  // Find the contiguous land range and pick its midpoint.
  let landStart = -1;
  let landEnd = -1;
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const p = { lat: a.lat + dlat * t, lon: a.lon + dlon * t };
    if (isOnLand(p)) {
      if (landStart < 0) landStart = i;
      landEnd = i;
    }
  }
  if (landStart < 0) return undefined;
  const midI = (landStart + landEnd) / 2;
  const t = midI / steps;
  return {
    deepest: { lat: a.lat + dlat * t, lon: a.lon + dlon * t },
  };
}

/**
 * Move `seed` perpendicular to the AB line (both directions) at
 * increasing distances until landing in water. Returns the first
 * water position found, or undefined if no nudge in the candidate
 * set escapes land.
 */
function nudgeOffLand(
  seed: LatLon,
  a: LatLon,
  b: LatLon,
  isOnLand: IsOnLand,
): LatLon | undefined {
  // Perpendicular bearing in degrees true: the segment direction is
  // bearing(a→b). Two perpendiculars are ±90°.
  const segCog = bearingApprox(a, b);
  for (const distNm of NUDGE_DISTANCES_NM) {
    for (const side of [+90, -90]) {
      const candidate = projectPosition(seed, segCog + side, distNm);
      if (!isOnLand(candidate)) return candidate;
    }
  }
  return undefined;
}

function bearingApprox(a: LatLon, b: LatLon): number {
  // Cheap planar bearing — accurate enough for nudge directions over
  // sub-nm scales. (Full geodesy is overkill for choosing a side.)
  const dlat = b.lat - a.lat;
  const dlon = (b.lon - a.lon) * Math.cos((a.lat * Math.PI) / 180);
  const rad = Math.atan2(dlon, dlat);
  return ((rad * 180) / Math.PI + 360) % 360;
}

function pathDistanceNm(path: ReadonlyArray<LatLon>): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) {
    total += distanceNm(path[i - 1]!, path[i]!);
  }
  return total;
}
